import React, {
  CSSProperties,
  Children,
  FC,
  isValidElement,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  useRef,
} from 'react';
import Slider, { Settings } from 'react-slick';
import { useWindowSize } from '../../features/DynamicLayout';
import { useTheme } from '../../../hooks/useTheme';
import SliderArrow, { ArrowPosition } from './SliderArrow';
import styles from './Carousel.module.css';

export interface CarouselProps {
  slickProps?: Settings;
  itemsCount: undefined | number;
  children: ReactNode;
  onArrowClick?: MouseEventHandler;
  carouselContainerStyles?: CSSProperties;
  carouselControlsContainerStyles?: CSSProperties;
}

const Carousel: FC<CarouselProps> = ({
  slickProps,
  itemsCount,
  children,
  onArrowClick = (): void => {},
  carouselContainerStyles = {},
  carouselControlsContainerStyles = {},
}) => {
  const customSlider = useRef<Slider>(null);

  const { bodytextDefaultNone } = useTheme();
  const dotsStyle: CSSProperties & { '--dot-color'?: string } =
    bodytextDefaultNone?.color
      ? { '--dot-color': bodytextDefaultNone.color }
      : {};

  const { dots, infinite, responsive } = slickProps || {};

  const windowWidth = useWindowSize().width || 0;

  // Sometimes the API does not send any responsive object, that's why it is necessary to validate that it exists.
  if (responsive)
    // Sort the elements from smallest to largest in breakpoints to later validate which object should be sent to you.
    responsive.sort((a, b) => {
      return a.breakpoint - b.breakpoint;
    });

  const responsiveObj = responsive
    ? responsive.find((responsiveObject) => {
        return windowWidth < responsiveObject.breakpoint;
      })
    : undefined;

  const showDots =
    responsiveObj &&
    typeof responsiveObj.settings === 'object' &&
    typeof responsiveObj.settings.dots !== 'undefined'
      ? responsiveObj.settings.dots
      : dots;

  const className =
    itemsCount && itemsCount <= 1
      ? styles.containerLessItems
      : styles.container;

  return (
    <div className={className} style={carouselContainerStyles}>
      <Slider
        ref={customSlider}
        appendDots={(sliderDots) => {
          const numDots = Children.count(sliderDots);
          let activeDotIndex;
          Children.forEach(sliderDots, (dot, i) => {
            if (
              isValidElement(dot) &&
              (dot as ReactElement<{ className: string }>).props.className ===
                'slick-active'
            ) {
              activeDotIndex = i;
            }
          });
          return (
            <div style={carouselControlsContainerStyles}>
              {/* The class `controls` cannot be added to above div because class is overridden by react-slick */}
              <div className={styles.controls}>
                <SliderArrow
                  onClick={(e): void => {
                    if (customSlider.current) customSlider.current.slickPrev();
                    if (onArrowClick) onArrowClick(e);
                  }}
                  // The default value for `infinite` is true. This means receiving
                  // `slickProps.infinite: undefined` actually means
                  // `infinite: true`. So, explicitly check for false.
                  disabled={infinite === false && activeDotIndex === 0}
                  arrowPosition={ArrowPosition.ARROW_PREV}
                />
                {showDots ? (
                  <ul className={styles.unorderedList} style={dotsStyle}>
                    {sliderDots}
                  </ul>
                ) : (
                  <></>
                )}
                <SliderArrow
                  onClick={(e): void => {
                    if (customSlider.current) customSlider.current.slickNext();
                    if (onArrowClick) onArrowClick(e);
                  }}
                  // The default value for `infinite` is true. This means receiving
                  // `slickProps.infinite: undefined` actually means
                  // `infinite: true`. So, explicitly check for false.
                  disabled={
                    infinite === false && activeDotIndex === numDots - 1
                  }
                  arrowPosition={ArrowPosition.ARROW_NEXT}
                />
              </div>
            </div>
          );
        }}
        arrows={false}
        speed={300}
        {...slickProps}
        // Dont remove the dots, this need to be always true because we put the arrows in the dots prop
        dots
        responsive={
          responsive &&
          responsive.map((breakpoint) => ({
            ...breakpoint,
            settings:
              typeof breakpoint.settings === 'object'
                ? {
                    ...breakpoint.settings,
                    dots: true,
                  }
                : breakpoint.settings,
          }))
        }
      >
        {children}
      </Slider>
    </div>
  );
};

export { SliderArrow, ArrowPosition };

export default Carousel;
